/**
 * PublicInspectionPage - Vistoria por link externo (/v/:token), mobile first
 *
 * Fluxo: confirmação de identidade -> capa (veículo, progresso, validade) -> itens por
 * seção em grade premium (fotos em duas colunas; demais tipos em linha inteira) ->
 * envio para revisão. Reaproveita a câmera da vistoria interna (CameraCaptureDialog),
 * a captura de localização (captureGeo) e a placa Mercosul. Todas as regras continuam
 * validadas no servidor.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-17 v1.17.0 - Aceite não é preparado de novo com a janela de assinatura aberta;
 *            pedido anulado no meio (respostas salvas) fecha a janela, recarrega e pede nova assinatura.
 *   RELEASE: 2026-10-14 v1.16.0 - Progresso real por foto (cartão e rodapé), fotos guardadas no
 *            aparelho com retomada ao reabrir o link e aceite liberado só após todos os envios.
 *   RELEASE: 2026-10-14 v1.15.0 - Fotos/anexos enviados em segundo plano: câmera e galeria
 *            liberam a tela na hora, o usuário avança para os próximos itens, cada cartão
 *            mostra "Enviando"/"Falhou — tocar para reenviar" e o rodapé o progresso geral.
 *            Envio para revisão bloqueado enquanto houver arquivo pendente.
 *   RELEASE: 2026-10-13 v1.14.0 - Aceite remoto a partir do link de execução: botão
 *            "Enviar link de aceite" (copiar + WhatsApp) junto ao aceite do cliente pendente,
 *            gerando o mesmo link exclusivo do desktop. Aceite do cliente com escolha
 *            Titular/Testemunha quando o modelo habilita (allow_witness).
 *   RELEASE: 2026-10-05 v1.13.0 - Aceite externo exibe todos os itens expandidos,
 *            abre evidências no preview interno e mostra Tipo/Modelo do checklist.
 *   RELEASE: 2026-10-05 v1.12.0 - Link exclusivo de aceite do cliente: resumo somente leitura
 *            dos itens e assinatura apenas do papel Cliente/proprietário (AcceptanceOnlyWorkspace).
 *   RELEASE: 2026-10-01 v1.11.0 - Nome do cliente/proprietário do veículo no cabeçalho,
 *            ao lado do número da vistoria ("Vistoria #N | Nome"), para confirmação
 *            visual de que o link está com a pessoa certa. Apenas apresentação.
 *   RELEASE: 2026-09-29 v1.10.0 - Foto de perfil real do veículo (ou ícone neutro) no lugar da
 *            imagem fixa; anel encaixado à direita do bloco do veículo e progresso em verde-creme.
 *   RELEASE: 2026-09-29 v1.9.0 - Cartão do veículo passa a exibir o progresso em
 *            anel circular (ProgressRing em SVG) com o % no centro, no lugar do
 *            texto simples. Apenas apresentação; contagem e barra mantidas.
 *   RELEASE: 2026-09-29 v1.8.0 - Remove progresso duplicado do cabeçalho, troca
 *            "Link seguro" por saída da sessão e adota hierarquia tipográfica
 *            Material/Roboto com badges em caixa normal e pesos moderados.
 *   RELEASE: 2026-09-29 v1.7.0 - Tema claro "Vidro moderno" aprovado: cabeçalho de app
 *            fixo com progresso, cartão do veículo verde-esmeralda com contagem e
 *            porcentagem, grade de fotos em duas colunas com selos de estado
 *            (Concluída/Aprovada/Próxima foto/Pendente/Refazer), itens não visuais em
 *            linha inteira com o campo na própria linha, rodapé contextual com ação
 *            "Fotografar/Responder próximo item" e "Salvar e continuar depois".
 *            Apenas apresentação; fluxo, validações e segurança intactos.
 *   RELEASE: 2026-09-29 v1.6.0 - Fidelidade ao mockup: paleta grafite+âmbar própria
 *            (.inspection-app, também no body p/ portais), moldura de app 480px,
 *            hero maior com progresso, cartão do veículo com chip de placa e
 *            validade por extenso, campo com rótulo flutuante, botões âmbar h-14.
 *   RELEASE: 2026-09-29 v1.5.0 - Design "Hero sobreposto" aprovado: tema escuro
 *            dedicado (escopo `dark`), hero fotográfico com identidade do tenant e
 *            selo "Link seguro", cartão do veículo sobreposto, cartão de identidade
 *            com ícone âmbar e rodapé de confiança (LGPD). Padrão visual mobile
 *            (Material Design 3). Apenas apresentação; fluxo e segurança intactos.
 *   RELEASE: 2026-09-29 v1.4.0 - Layout premium: painel lateral com fotografia
 *            automotiva (desktop) e faixa de topo (mobile), fiel às imagens
 *            geradas em src/assets/inspection-public. Apenas apresentação;
 *            fluxo, validações e segurança do link permanecem intactos.
 *   RELEASE: 2026-09-28 v1.3.0 - Acesso por e-mail para usuário responsável e CPF/CNPJ para proprietário.
 *   RELEASE: 2026-09-28 v1.2.0 - Cartões premium e seletor móvel com foto modelo.
 *   RELEASE: 2026-09-27 v1.1.0 - Bloco "Aceites necessários" reaproveitando AcceptanceDialog.
 *   RELEASE: 2026-09-27 v1.0.0 - Criação inicial.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInspectionUploadQueue } from '@/hooks/useInspectionUploadQueue';
import { aggregateProgress, type InspectionUploadQueue, type UploadTask } from '@/lib/inspectionUploadQueue';
import type { PublicAttachment } from '@/hooks/usePublicChecklist';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Camera, Check, Loader2, Lock, LogOut, Paperclip, Send, ShieldCheck, AlertTriangle, PlayCircle, Save, ArrowRight, Car, CalendarDays, FileText, Mail, User, Link2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { CameraCaptureDialog } from '@/components/checklist/inspection/CameraCaptureDialog';
import { captureGeo } from '@/hooks/useVehicleInspections';
import { AcceptanceDialog } from '@/components/acceptance/AcceptanceDialog';
import { isStaleAcceptanceError, STALE_ACCEPTANCE_MESSAGE } from '@/lib/acceptance/staleAcceptance';
import { ROLE_LABEL, type AcceptanceParticipant, type PreparedAcceptance } from '@/hooks/useChecklistAcceptance';
import { usePublicChecklist, type PublicItem, type PublicLinkState } from '@/hooks/usePublicChecklist';
import { InspectionEvidenceDrawer } from '@/components/checklist/inspection/InspectionEvidenceDrawer';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { resolvePreviewMediaType } from '@/lib/attachment-preview';
import { DataRefreshButton } from '@/components/common/DataRefreshButton';
// Fotografia usada como miniatura ilustrativa do veículo no cartão de progresso

const STATE_TEXT: Record<Exclude<PublicLinkState, 'active'>, { title: string; text: string }> = {
  submitted: { title: 'Vistoria enviada', text: 'Recebemos sua vistoria. Ela está em análise pela equipe.' },
  expired: { title: 'Link expirado', text: 'Este link não está mais válido. Solicite um novo link à empresa.' },
  revoked: { title: 'Link desativado', text: 'Este link foi substituído ou desativado. Solicite um novo link à empresa.' },
  closed: { title: 'Vistoria encerrada', text: 'Esta vistoria já foi finalizada e não aceita alterações.' },
  invalid: { title: 'Link inválido', text: 'Confira se o endereço foi copiado corretamente.' },
};

const fmt = (d?: string | null) => (d ? format(new Date(d), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : '—');

/** Validade por extenso: "14 de mai. de 2026". */
const fmtLong = (d?: string | null) => (d ? format(new Date(d), "dd 'de' MMM'.' 'de' yyyy", { locale: ptBR }).replace('..', '.') : '—');

/** Tipos visuais: ocupam células da grade de fotos. */
const VISUAL_TYPES = ['file', 'photo', 'photo_or_file'];

/** Item respondido: tem anexo ou valor vigente. */
const isAnswered = (it: PublicItem) => it.attachments.length > 0 || (it.current && it.current.value !== null && it.current.value !== '');

export default function PublicInspectionPage() {
  const { token } = useParams<{ token: string }>();
  const pc = usePublicChecklist(token);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Não remonta Workspace: respostas locais, fotos e assinatura não são apagadas.
  const refresh = async () => {
    if (refreshing || busy) return;
    setRefreshing(true);
    try { await pc.refresh(); }
    catch (e) { toast.error((e as Error).message || 'Não foi possível atualizar a vistoria.'); }
    finally { setRefreshing(false); }
  };
  const r = pc.resolved;

  // v1.7.0: tema claro do app no <body> para alcançar gavetas e diálogos (portais)
  useEffect(() => {
    document.body.classList.add('inspection-app');
    return () => { document.body.classList.remove('inspection-app'); };
  }, []);

  if (pc.loading) {
    return <div className="inspection-app flex min-h-dvh items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const state = r?.state ?? 'invalid';
  const showWork = pc.hasSession && pc.detail && (state === 'active');
  // v1.12.0: propósito definido pelo servidor (o cliente não escolhe)
  const isAcceptanceLink = r?.purpose === 'acceptance';
  const det = pc.detail;
  const doneCount = det ? det.items.filter(isAnswered).length : 0;
  const totalCount = det ? det.items.length : 0;
  return (
    // Fundo externo suave em volta da coluna de celular (desktop)
    <div className="inspection-app min-h-dvh bg-muted/40 text-foreground">
      <div className="relative mx-auto min-h-dvh w-full max-w-[480px] bg-background sm:shadow-2xl sm:ring-1 sm:ring-border/60">
        <AppHeader tenant={r?.tenant} code={r?.instance?.code} ownerName={pc.ownerName} canExit={pc.hasSession} onExit={pc.exitSession} onRefresh={refresh} refreshing={refreshing || busy} />
        <main className="space-y-4 px-4 pb-44 pt-4">
          {r?.vehicle && state !== 'invalid' && (
            <VehicleCard vehicle={r.vehicle} expiresAt={state === 'active' ? r.expires_at : null}
              done={doneCount} total={totalCount} showProgress={!!det}
              photoUrl={((det?.vehicle?.photo_url ?? (r.vehicle as { photo_url?: string | null }).photo_url) as string | null | undefined) ?? null} />
          )}

          {state !== 'active' && <StateCard state={state} />}

          {state === 'active' && !pc.hasSession && (
            <IdentityGate kind={r?.identity_kind ?? 'document'} onVerify={pc.verifyIdentity} acceptanceOnly={isAcceptanceLink} />
          )}
          {state === 'active' && pc.hasSession && !pc.detail && (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          )}
          {showWork && det && <ChecklistInfoCard instance={det.instance} />}
          {/* v1.12.0: link exclusivo de aceite do cliente (somente leitura + assinatura) */}
          {showWork && (isAcceptanceLink
            ? <AcceptanceOnlyWorkspace pc={pc} />
            : <Workspace pc={pc} busy={busy} setBusy={setBusy} />)}

          {/* Rodapé de confiança: ícones verdes, texto em duas linhas e divisores */}
          <footer className="grid grid-cols-3 divide-x divide-border pt-6">
            {[
              { icon: ShieldCheck, label: 'Seus dados protegidos' },
              { icon: FileText, label: 'Processo 100% seguro' },
              { icon: Lock, label: 'Conformidade com a LGPD' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 px-2">
                <Icon className="h-5 w-5 shrink-0 text-primary" />
                <span className="text-[11px] leading-tight text-muted-foreground">{label}</span>
              </div>
            ))}
          </footer>
        </main>
      </div>
    </div>
  );
}

/** Identificação do checklist carregado: evita dúvidas entre tipo e modelo no link externo. */
function ChecklistInfoCard({ instance }: { instance: { checklist_type_name?: string | null; template_name?: string | null } }) {
  return (
    <section className="grid grid-cols-1 gap-2 rounded-2xl border bg-card p-3 shadow-sm">
      <div className="flex items-start gap-2">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase text-muted-foreground">Tipo de checklist</p>
          <p className="truncate text-sm font-semibold text-foreground">{instance.checklist_type_name || '—'}</p>
        </div>
      </div>
      <div className="flex items-start gap-2 border-t pt-2">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase text-muted-foreground">Modelo de checklist</p>
          <p className="truncate text-sm font-semibold text-foreground">{instance.template_name || '—'}</p>
        </div>
      </div>
    </section>
  );
}

/** Cabeçalho de app: identidade do tenant, vistoria e cliente (v1.11.0) e saída da sessão. */
function AppHeader({ tenant, code, ownerName, canExit, onExit, onRefresh, refreshing }: {
  tenant?: { name?: string | null; logo_url?: string | null } | null;
  code?: number | null;
  /** Nome do cliente/proprietário do veículo (resolve). Exibido após o número. */
  ownerName?: string | null;
  canExit: boolean;
  onExit: () => void;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <header className="sticky top-0 z-30 border-b border-border/60 bg-background/95 px-4 py-3 backdrop-blur">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {tenant?.logo_url
            ? <img src={tenant.logo_url} alt={tenant.name ?? 'Logo'} className="h-9 w-9 rounded-xl border border-border/60 bg-white object-contain p-1" />
            : <ShieldCheck className="h-9 w-9 text-primary" />}
          <div className="min-w-0 leading-tight">
            <p className="truncate text-sm font-semibold text-foreground">{tenant?.name ?? 'Vistoria veicular'}</p>
            {/* v1.11.0: "Vistoria #N | Nome do cliente" — truncamento p/ não quebrar o layout */}
            {(code || ownerName) && (
              <p className="truncate text-xs font-normal text-muted-foreground">
                {code && `Vistoria #${code}`}
                {code && ownerName && ' | '}
                <span className="font-medium text-foreground/80">{ownerName}</span>
              </p>
            )}
          </div>
        </div>
        <DataRefreshButton onRefresh={onRefresh} refreshing={refreshing} />
        {canExit && (
          <Button type="button" variant="ghost" size="sm" className="h-10 shrink-0 gap-2 px-3 text-sm font-medium text-muted-foreground" onClick={onExit}>
            <LogOut className="h-4 w-4" /> Sair
          </Button>
        )}
      </div>
    </header>
  );
}

/**
 * Anel circular de progresso (SVG puro, sem dependência): círculo de fundo
 * translúcido + arco branco com pontas arredondadas e o % em negrito no centro.
 * Usado no cartão do veículo para dar cara de aplicativo mobile ao progresso.
 */
function ProgressRing({ pct, size = 64 }: { pct: number; size?: number }) {
  const stroke = 5; // espessura do anel
  const r = (size - stroke) / 2; // raio útil descontando a espessura
  const c = 2 * Math.PI * r; // circunferência para calcular o arco preenchido
  const filled = c * (Math.min(100, Math.max(0, pct)) / 100);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} role="img" aria-label={`${pct}% concluído`}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Trilho de fundo do anel */}
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-[hsl(var(--inspection-progress)/0.22)]" />
        {/* Arco de progresso com transição suave */}
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke}
          strokeLinecap="round" className="stroke-[hsl(var(--inspection-progress))] transition-all duration-700"
          strokeDasharray={`${filled} ${c - filled}`}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold">{pct}%</span>
    </div>
  );
}

/** Cartão do veículo: verde-escuro com foto de perfil (ou ícone), placa, anel e barra. */
function VehicleCard({ vehicle, expiresAt, done, total, showProgress, photoUrl }: {
  vehicle: { plate: string | null; brand: string | null; model: string | null; year: string | null };
  expiresAt?: string | null;
  done: number;
  total: number;
  showProgress: boolean;
  /** v1.10.0: URL assinada da foto de perfil do veículo (null = ícone neutro) */
  photoUrl?: string | null;
}) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <section className="relative overflow-hidden rounded-3xl bg-primary p-5 text-primary-foreground shadow-xl shadow-primary/20">
      {/* Brilho decorativo no canto inferior direito */}
      <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
      {/* Linha superior: foto + identificação à esquerda, anel à direita (v1.10.0) */}
      <div className="relative z-10 flex items-center gap-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white/10">
          {photoUrl
            ? <img src={photoUrl} alt="Foto do veículo" className="h-full w-full object-cover" />
            : <Car className="h-9 w-9 text-primary-foreground/60" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-normal text-primary-foreground/70">Veículo</p>
          <h2 className="line-clamp-2 text-base font-semibold leading-snug">{[vehicle.brand, vehicle.model].filter(Boolean).join(' ') || 'Veículo'}</h2>
          <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg border border-primary-foreground/25 bg-primary-foreground/10 px-2.5 py-1 text-sm font-medium text-primary-foreground">
            <Car className="h-4 w-4" /> {vehicle.plate ?? '—'}
          </span>
        </div>
        {showProgress && <ProgressRing pct={pct} />}
      </div>
      {showProgress && (
        <div className="relative z-10 mt-4">
          <span className="text-xs font-normal text-primary-foreground/75">{done} de {total} concluídos</span>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[hsl(var(--inspection-progress)/0.22)]">
            <div className="h-full rounded-full bg-[hsl(var(--inspection-progress))] transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          {expiresAt && (
            <p className="mt-2 flex items-center gap-1.5 text-[11px] text-primary-foreground/70">
              <CalendarDays className="h-3.5 w-3.5" /> Válido até {fmtLong(expiresAt)}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/** Cartão de estado do link (expirado, revogado, enviado...). */
function StateCard({ state }: { state: Exclude<PublicLinkState, 'active'> }) {
  const s = STATE_TEXT[state];
  return (
    <section className="rounded-3xl border bg-card p-6 text-center shadow-sm">
      <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
        {state === 'submitted' ? <Check className="h-6 w-6" /> : <Lock className="h-6 w-6" />}
      </span>
      <h1 className="text-xl font-semibold">{s.title}</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">{s.text}</p>
    </section>
  );
}

/** Confirma a identidade congelada na geração do link (campo com rótulo flutuante). */
function IdentityGate({ kind, onVerify, acceptanceOnly = false }: { kind: 'document' | 'email'; onVerify: (identity: string) => Promise<void>; acceptanceOnly?: boolean }) {
  const [identity, setIdentity] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const digits = identity.replace(/\D/g, '');
  const normalized = kind === 'email' ? identity.trim().toLowerCase() : digits;
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized) && normalized.length <= 254;
  const valid = kind === 'email' ? validEmail : digits.length === 11 || digits.length === 14;
  const FieldIcon = kind === 'email' ? Mail : User;

  const submit = async () => {
    setSending(true); setError(null);
    try { await onVerify(normalized); } catch (e) { setError((e as Error).message); } finally { setSending(false); }
  };

  return (
    <section className="space-y-5 rounded-3xl border bg-card p-5 shadow-sm">
      <div className="flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
          <User className="h-6 w-6" />
        </span>
        <div>
          <h1 className="text-xl font-semibold">Confirme sua identidade</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {acceptanceOnly
              ? 'Informe o CPF (ou CNPJ) do proprietário para conferir a vistoria e dar o seu aceite.'
              : kind === 'email'
              ? 'Informe o e-mail cadastrado do responsável para acessar a vistoria.'
              : 'Informe o CPF (ou CNPJ) do proprietário para acessar a vistoria deste veículo.'}
          </p>
        </div>
      </div>
      <label className="relative block">
        <span className="absolute -top-2.5 left-4 bg-card px-1.5 text-xs font-medium text-muted-foreground">
          {kind === 'email' ? 'E-mail' : 'CPF / CNPJ'}
        </span>
        <FieldIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input type={kind === 'email' ? 'email' : 'text'} inputMode={kind === 'email' ? 'email' : 'numeric'}
          autoComplete={kind === 'email' ? 'email' : 'off'} placeholder={kind === 'email' ? 'seu@email.com' : '000.000.000-00'}
          maxLength={kind === 'email' ? 254 : 18} value={identity}
          onChange={(e) => setIdentity(kind === 'email' ? e.target.value.slice(0, 254) : e.target.value.replace(/[^\d.\-/]/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter' && valid && !sending) submit(); }}
          className="h-14 rounded-2xl border-input bg-transparent pl-12 text-base focus-visible:ring-warning" />
      </label>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button className="h-14 w-full justify-between rounded-2xl px-6 text-base font-medium shadow-lg disabled:opacity-60" disabled={sending || !valid} onClick={submit}>
        <span className="flex-1 text-center">{sending && <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />}Acessar vistoria</span>
        <ArrowRight className="h-5 w-5" />
      </Button>
    </section>
  );
}

type PC = ReturnType<typeof usePublicChecklist>;

/** v1.15.0: fila de envios compartilhada entre a área da vistoria e os cartões dos itens. */
type UploadsValue = {
  queue: InspectionUploadQueue<PublicAttachment>;
  tasks: UploadTask<PublicAttachment>[];
  geo: Map<string, Promise<unknown>>;
};
const UploadCtx = createContext<UploadsValue | null>(null);

/**
 * v1.12.0: link exclusivo de aceite do cliente.
 * Mostra o resumo somente leitura dos itens e permite assinar apenas como Cliente/proprietário.
 * O servidor bloqueia qualquer outra ação deste link (Edge Function + RPC).
 */
function AcceptanceOnlyWorkspace({ pc }: { pc: PC }) {
  const d = pc.detail!;
  const [acc, setAcc] = useState<PreparedAcceptance | null>(null);
  const [signing, setSigning] = useState<AcceptanceParticipant | null>(null);
  const [lightbox, setLightbox] = useState<{ items: MediaItem[]; index: number; caption: string } | null>(null);

  const loadAcc = useCallback(async () => {
    try { setAcc(await pc.prepareAcceptance()); } catch (e) {
      setAcc(null);
      toast.error('Não foi possível carregar o aceite. Recarregue a página.');
      logger.warn('Falha ao carregar aceite do cliente', (e as Error)?.message);
    }
  }, [pc.prepareAcceptance]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadAcc(); }, [loadAcc]);

  // Somente participantes que podem assinar por este link (servidor define can_sign_here)
  const owners = (acc?.participants ?? []).filter((p) => (p as { can_sign_here?: boolean }).can_sign_here);
  const allDone = owners.length > 0 && owners.every((p) => p.status === 'accepted');

  /** Texto curto da resposta de um item (somente leitura). */
  const answerText = (it: PublicItem) => {
    if (it.attachments.length) return `${it.attachments.length} foto(s)/arquivo(s)`;
    const v = it.current?.value;
    if (v === null || v === undefined || v === '') return 'Sem resposta';
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
    return String(v);
  };

  /** Converte anexos vigentes em itens aceitos pelo preview compartilhado. */
  const mediaFor = (it: PublicItem): MediaItem[] => it.attachments
    .filter((a) => !!a.url)
    .map((a) => ({
      url: a.url as string,
      type: resolvePreviewMediaType(a.mime_type?.startsWith('image/') ? 'image' : undefined, a.mime_type) ?? 'document',
      title: a.file_name,
    }));

  return (
    <>
      <section className="space-y-3 rounded-3xl border bg-card p-4 shadow-sm">
        <h2 className="text-base font-semibold">Aceite da vistoria</h2>
        <p className="text-sm text-muted-foreground">
          Confira o resumo da vistoria realizada pela equipe e registre seu aceite.
        </p>
        {!acc && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
        {acc && owners.length === 0 && (
          <p className="text-sm text-muted-foreground">Não há aceite pendente para você nesta vistoria.</p>
        )}
        {owners.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 rounded-xl border bg-muted/40 px-3 py-2 text-sm">
            <div className="min-w-0">
              <p className="font-medium">{ROLE_LABEL[p.role]}</p>
              <p className="truncate text-xs text-muted-foreground">
                {p.status === 'accepted' ? `Assinado por ${p.signer_name ?? '—'}` : 'Aguardando seu aceite'}
              </p>
            </div>
            {p.status === 'accepted'
              ? <Check className="h-5 w-5 text-success" />
              : <Button size="sm" className="h-9" onClick={() => setSigning(p)}>Dar aceite</Button>}
          </div>
        ))}
        {allDone && <p className="text-sm font-medium text-success">Aceite registrado. Obrigado!</p>}
      </section>

      <section className="space-y-2">
        <h3 className="px-1 text-sm font-semibold text-foreground">Resumo da vistoria</h3>
        {d.items.map((it) => (
          <ExpandedAcceptanceItem
            key={it.answer_id}
            item={it}
            answerText={answerText(it)}
            mediaItems={mediaFor(it)}
            onPreview={(items, index) => setLightbox({ items, index, caption: it.item.name })}
          />
        ))}
      </section>
      {lightbox && <ImageLightbox open onClose={() => setLightbox(null)} mediaItems={lightbox.items} initialIndex={lightbox.index} caption={lightbox.caption} />}

      {signing && acc?.request_id && (
        <AcceptanceDialog open={!!signing} onOpenChange={(v) => { if (!v) { setSigning(null); loadAcc(); } }}
          requestId={acc.request_id} participant={signing}
          requirements={signing.requirements ?? acc.requirements!} terms={signing.terms ?? acc.terms!}
          defaultName={d.vehicle.owner_name ?? ''}
          allowWitness={!!acc.allow_witness}
          onConfirm={async (input) => {
            try { await pc.confirmAcceptance(input); } catch (e) {
              // v1.17.0: pedido anulado no meio → recarrega e pede nova assinatura
              if (isStaleAcceptanceError((e as Error)?.message)) { setSigning(null); loadAcc(); throw new Error(STALE_ACCEPTANCE_MESSAGE); }
              throw e;
            }
            toast.success('Aceite registrado');
          }} />
      )}
    </>
  );
}

/**
 * v1.14.0: gera e compartilha o link exclusivo de aceite do cliente a partir do
 * link de execução (copiar para a área de transferência ou abrir no WhatsApp).
 * O servidor gera o mesmo link do desktop e revalida sessão e aceite pendente.
 */
function AcceptanceLinkShare({ pc, disabled = false }: { pc: PC; disabled?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await pc.generateAcceptanceLink();
      setUrl(r.url);
      try { await navigator.clipboard.writeText(r.url); toast.success('Link de aceite copiado'); }
      catch { toast.success('Link de aceite gerado'); }
    } catch (e) {
      toast.error((e as Error).message || 'Não foi possível gerar o link de aceite');
    } finally { setBusy(false); }
  };

  const whatsapp = () => {
    if (!url) return;
    const text = encodeURIComponent(`Olá! Segue o link para conferir e assinar o aceite da vistoria: ${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border/60 pt-2">
      {!url ? (
        <Button type="button" size="sm" variant="outline" className="h-8 text-xs" disabled={busy || disabled} onClick={generate}>
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Link2 className="mr-1.5 h-3.5 w-3.5" />}
          Enviar link de aceite
        </Button>
      ) : (
        <>
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs" disabled={disabled} onClick={async () => {
            try { await navigator.clipboard.writeText(url); toast.success('Link copiado'); } catch { toast.error('Não foi possível copiar'); }
          }}>
            <Link2 className="mr-1.5 h-3.5 w-3.5" /> Copiar link
          </Button>
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs" disabled={disabled} onClick={whatsapp}>
            <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
          </Button>
        </>
      )}
      <p className="w-full text-[11px] text-muted-foreground">
        O cliente abre o link, confirma o CPF/CNPJ e assina de qualquer lugar.
      </p>
    </div>
  );
}

/** Item do resumo do aceite: já vem expandido para agilizar a conferência das evidências. */
function ExpandedAcceptanceItem({ item, answerText, mediaItems, onPreview }: {
  item: PublicItem;
  answerText: string;
  mediaItems: MediaItem[];
  onPreview: (items: MediaItem[], index: number) => void;
}) {
  return (
    <article className="space-y-2 rounded-2xl border bg-card p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{item.item.name}</p>
          {item.item.description && <p className="mt-0.5 text-xs text-muted-foreground">{item.item.description}</p>}
        </div>
        <span className="max-w-[44%] shrink-0 truncate text-right text-xs text-muted-foreground">{answerText}</span>
      </div>
      {mediaItems.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {mediaItems.map((media, index) => {
            const isImage = media.type === 'image';
            return (
              <Button
                key={`${media.url}-${index}`}
                type="button"
                variant="ghost"
                className="h-32 overflow-hidden rounded-xl border bg-muted p-0 hover:bg-muted"
                onClick={() => onPreview(mediaItems, index)}
                title="Abrir preview"
              >
                {isImage
                  ? <img src={media.url} alt={media.title ?? item.item.name} loading="lazy" className="h-full w-full object-cover" />
                  : <span className="flex h-full w-full flex-col items-center justify-center gap-2 px-2 text-xs text-muted-foreground"><Paperclip className="h-5 w-5" />{media.title ?? 'Arquivo'}</span>}
              </Button>
            );
          })}
        </div>
      )}
    </article>
  );
}

/** Área de preenchimento: iniciar, itens por seção e envio. */
function Workspace({ pc, busy, setBusy }: { pc: PC; busy: boolean; setBusy: (v: boolean) => void }) {
  const d = pc.detail!;
  const status = d.instance.status;
  const geoPerItem = ['item', 'item_and_submit'].includes(d.version.settings.geo_mode ?? '');
  const geoOnSubmit = ['submit', 'item_and_submit'].includes(d.version.settings.geo_mode ?? '');
  const geoRequired = !!d.version.settings.geo_required;
  // 2026-10-10: modelo sem revisão conclui no envio; aceites de aprovação/conclusão também são coletados aqui
  const submitStages = d.version.settings.requires_review === false
    ? ['before_submit', 'before_review', 'before_approve', 'before_complete'] : ['before_submit', 'before_review'];
  const editable = status === 'in_progress' || status === 'correction_requested';

  const total = d.items.length;
  const done = d.items.filter(isAnswered).length;
  const missing = d.items.filter((it) => it.item.is_required && !isAnswered(it));
  const rejectedPending = d.items.filter((it) => it.review_status === 'rejected');

  // =========================================================================
  // v1.15.0: fila de envio em segundo plano (fotos/anexos)
  // =========================================================================
  const detailRef = useRef(d);
  detailRef.current = pc.detail ?? d;
  const geoRef = useRef(new Map<string, Promise<unknown>>());
  // Última lista de anexos gravada por item nesta sessão: evita perder anexos quando duas
  // gravações seguidas do mesmo item ocorrem antes de a tela recarregar os dados.
  const savedRef = useRef(new Map<string, PublicAttachment[]>());
  const { token: linkToken = '' } = useParams<{ token: string }>();
  const { queue, tasks, hasPending, storeAvailable, restoreFromDevice } = useInspectionUploadQueue<PublicAttachment>({
    linkToken,
    upload: (t, onProgress) => pc.uploadFile(t.answerId, t.file, t.fromCamera, onProgress),
    save: async (answerId, uploaded) => {
      const cur = detailRef.current.items.find((i) => i.answer_id === answerId);
      if (!cur) throw new Error('Item não encontrado');
      const maxFiles = Math.max(1, Number(cur.item.config.max_files) || 20);
      const base = savedRef.current.get(answerId) ?? cur.attachments.map(({ url: _u, ...a }) => a as PublicAttachment);
      const kept = maxFiles > 1 ? base : [];
      const geo = geoPerItem ? await (geoRef.current.get(answerId) ?? captureGeo()) : null;
      if (geoPerItem && geoRequired && !geo) {
        geoRef.current.delete(answerId); // nova tentativa captura a localização de novo
        throw new Error('Permita o acesso à localização para salvar');
      }
      const next = [...kept, ...uploaded].slice(-maxFiles);
      await pc.act({ action: 'save_answer', answer_id: answerId, value: null, geo, attachments: next });
      savedRef.current.set(answerId, next);
    },
  });
  // v1.16.0: retoma arquivos guardados no aparelho (página fechada antes de terminar o envio).
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !linkToken) return;
    restoredRef.current = true;
    const canReceive = (answerId: string) => {
      const it = d.items.find((i) => i.answer_id === answerId);
      return !!it && editable && VISUAL_TYPES.includes(it.item.field_type) && (status !== 'correction_requested' || it.review_status === 'rejected');
    };
    void restoreFromDevice(canReceive).then(({ restored, discarded }) => {
      if (restored) toast(`Retomando o envio de ${restored} arquivo(s)`);
      if (discarded) toast.warning(`${discarded} arquivo(s) guardado(s) não puderam ser enviados: a vistoria não aceita mais envio nesses itens.`);
    });
  }, [linkToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const doneUploads = tasks.filter((t) => t.status === 'done').length;
  const overallProgress = aggregateProgress(tasks);
  const failedCount = tasks.filter((t) => t.status === 'error').length;
  const sentAll = tasks.length > 0 && doneUploads === tasks.length;
  // Tudo enviado: mostra "Todos os arquivos enviados" por 2,5 s e limpa a barra.
  useEffect(() => {
    if (!sentAll) return;
    const t = setTimeout(() => {
      queue.list.forEach((x) => x.status === 'done' && x.previewUrl && URL.revokeObjectURL(x.previewUrl));
      queue.clearDone();
    }, 2500);
    return () => clearTimeout(t);
  }, [sentAll, queue]);
  const uploadsValue = useMemo(() => ({ queue, tasks, geo: geoRef.current }), [queue, tasks]);

  // Item com arquivo na fila não é mais "o próximo": o usuário avança enquanto envia.
  const queuedIds = new Set(tasks.filter((t) => t.status !== 'done').map((t) => t.answerId));
  const nextMissing = editable ? missing.find((it) => !queuedIds.has(it.answer_id)) ?? null : null;

  const groups = useMemo(() => {
    const bySection = d.sections.map((s) => ({ ...s, items: d.items.filter((i) => i.item.version_section_id === s.id) }));
    const loose = d.items.filter((i) => !i.item.version_section_id);
    return [...(loose.length ? [{ id: 'none', name: 'Itens', sort_order: -1, items: loose }] : []), ...bySection].filter((g) => g.items.length);
  }, [d]);

  const run = async (fn: () => Promise<void>, ok?: string) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };

  // v1.1.0: aceites exigidos antes do envio
  const [acc, setAcc] = useState<PreparedAcceptance | null>(null);
  const [signing, setSigning] = useState<AcceptanceParticipant | null>(null);
  const [openId, setOpenId] = useState<string | null>(null); // v1.7.0: item alvo do botão principal
  const loadAcc = useCallback(async () => {
    // v1.9.1: falha ao carregar aceites não fica mais silenciosa (antes o bloco apenas sumia)
    try { setAcc(await pc.prepareAcceptance()); } catch (e) {
      setAcc(null);
      toast.error('Não foi possível carregar os aceites. Recarregue a página para tentar novamente.');
      logger.warn('Falha ao carregar aceites do link externo', (e as Error)?.message);
    }
  }, [pc.prepareAcceptance]); // eslint-disable-line react-hooks/exhaustive-deps
  // v1.17.0: com a janela de assinatura aberta não prepara novo aceite (anularia o participante em uso);
  // ao fechar, onOpenChange recarrega uma vez.
  useEffect(() => { if (editable && missing.length === 0 && !signing) loadAcc(); }, [editable, missing.length, d, loadAcc]); // eslint-disable-line react-hooks/exhaustive-deps
  const accPending = (acc?.required ? acc.participants ?? [] : []).filter((p) =>
    p.is_required && p.status !== 'accepted' && submitStages.includes(p.required_stage as string));

  const start = () => run(() => pc.act({ action: 'start' }), 'Vistoria iniciada');
  const submit = () => run(async () => {
    const geo = geoOnSubmit ? await captureGeo() : null;
    if (geoOnSubmit && geoRequired && !geo) throw new Error('Permita o acesso à localização para enviar');
    await pc.act({ action: 'submit', geo });
  }, 'Vistoria enviada para revisão');

  /** Abre a evidência/campo do próximo item pendente (rola até ele). */
  const openNext = () => { if (nextMissing) setOpenId(nextMissing.answer_id); };

  const nextIsVisual = nextMissing ? VISUAL_TYPES.includes(nextMissing.item.field_type) : false;

  return (
    <UploadCtx.Provider value={uploadsValue}>
      {status === 'pending' && (
        <Button className="h-14 w-full rounded-2xl text-base font-medium shadow-lg" disabled={busy} onClick={start}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />} Iniciar vistoria
        </Button>
      )}

      {status === 'correction_requested' && (
        <p className="flex items-start gap-1.5 rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {rejectedPending.length} item(ns) precisam ser refeitos. Registre novas evidências e envie novamente.
        </p>
      )}

      {groups.map((g) => (
        <section key={g.id} className="space-y-3">
          <header className="px-1 pt-2">
            <h3 className="text-lg font-semibold text-foreground">{g.name}</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {g.items.some((i) => VISUAL_TYPES.includes(i.item.field_type))
                ? 'Capture os ângulos indicados para concluir a vistoria.'
                : 'Preencha as informações solicitadas.'}
            </p>
          </header>
          {/* Grade: itens visuais em duas colunas; demais ocupam a linha inteira */}
          <div className="grid grid-cols-2 gap-3">
            {g.items.map((it, itemIndex) => (
              <ItemCard key={it.answer_id} it={it} pc={pc}
                itemNumber={d.items.findIndex((candidate) => candidate.answer_id === it.answer_id) + 1 || itemIndex + 1}
                editable={editable && (status !== 'correction_requested' || it.review_status === 'rejected')}
                geoPerItem={geoPerItem} geoRequired={geoRequired}
                isNext={nextMissing?.answer_id === it.answer_id}
                openRequest={openId === it.answer_id}
                onRequestHandled={() => setOpenId(null)} />
            ))}
          </div>
        </section>
      ))}

      {editable && missing.length === 0 && acc?.required && (acc.participants ?? []).some((p) => submitStages.includes(p.required_stage as string)) && (
        <section className="space-y-2 rounded-3xl border bg-card p-4 shadow-sm">
          <h2 className="text-sm font-semibold">Aceites necessários</h2>
          <p className="text-xs text-muted-foreground">Assine antes de enviar a vistoria para revisão.</p>
          {/* v1.16.0: aceite só depois de todas as evidências salvas (evita aceite com informação faltando) */}
          {hasPending && (
            <p className="flex items-start gap-1.5 border-l-2 border-warning pl-2 text-xs text-foreground">
              {failedCount ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" /> : <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-warning" />}
              <span>
                {failedCount ? `${failedCount} arquivo(s) não enviado(s). ` : 'Aguarde o envio de todos os arquivos para liberar o aceite.'}
                {failedCount > 0 && <button type="button" className="font-medium text-primary underline-offset-2 hover:underline" onClick={() => queue.retryAll()}>Tentar novamente</button>}
              </span>
            </p>
          )}
          {(acc.participants ?? []).filter((p) => submitStages.includes(p.required_stage as string)).map((p) => {
            const signable = (p as { can_sign_here?: boolean }).can_sign_here;
            return (
              <div key={p.id} className="rounded-xl border bg-muted/40 px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium">{ROLE_LABEL[p.role]}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {p.status === 'accepted' ? `Assinado por ${p.signer_name ?? '—'}` : signable ? 'Pendente' : 'Coletado pela equipe'}
                    </p>
                  </div>
                  {p.status === 'accepted'
                    ? <Check className="h-5 w-5 text-success" />
                    : signable && <Button size="sm" className="h-9" disabled={hasPending} onClick={() => setSigning(p)}>Assinar</Button>}
                </div>
                {/* v1.14.0: alternativa ao presencial — enviar o link exclusivo de aceite ao cliente */}
                {p.status !== 'accepted' && p.role === 'owner' && (
                  <AcceptanceLinkShare pc={pc} disabled={hasPending} />
                )}
              </div>
            );
          })}
        </section>
      )}

      {signing && acc?.request_id && (
        <AcceptanceDialog open={!!signing} onOpenChange={(v) => { if (!v) { setSigning(null); loadAcc(); } }}
          requestId={acc.request_id} participant={signing}
          requirements={signing.requirements ?? acc.requirements!} terms={signing.terms ?? acc.terms!}
          defaultName={signing.role === 'owner' ? (d.vehicle.owner_name ?? '') : ''}
          allowWitness={!!acc.allow_witness}
          onConfirm={async (input) => {
            // v1.16.0: envio pendente/falho surgido com a janela aberta impede o aceite
            if (queue.hasPending) throw new Error('Aguarde o envio de todos os arquivos antes de assinar.');
            try { await pc.confirmAcceptance(input); } catch (e) {
              // v1.17.0: pedido anulado no meio → recarrega e pede nova assinatura
              if (isStaleAcceptanceError((e as Error)?.message)) { setSigning(null); loadAcc(); throw new Error(STALE_ACCEPTANCE_MESSAGE); }
              throw e;
            }
            toast.success('Aceite registrado');
          }} />
      )}

      {/* Rodapé contextual: ação do próximo item pendente ou envio para revisão */}
      {editable && (
        <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-[480px] border-t border-border/60 bg-background/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
          {/* v1.15.0: progresso geral dos envios em segundo plano */}
          {tasks.length > 0 && (
            <div className="mb-2.5 space-y-1">
              <div className="flex items-center justify-between gap-2 text-xs font-medium">
                <span className={cn('flex items-center gap-1.5', failedCount ? 'text-destructive' : sentAll ? 'text-success' : 'text-foreground')}>
                  {failedCount ? <AlertTriangle className="h-3.5 w-3.5" /> : sentAll ? <Check className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {failedCount ? `${failedCount} arquivo(s) não enviado(s)` : sentAll ? 'Todos os arquivos enviados' : `Enviando ${doneUploads} de ${tasks.length} arquivo(s) · ${Math.round(overallProgress * 100)}%`}
                </span>
                {failedCount > 0 && (
                  <button type="button" className="font-medium text-primary underline-offset-2 hover:underline" onClick={() => queue.retryAll()}>Tentar novamente</button>
                )}
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div className={cn('h-full rounded-full transition-all', failedCount ? 'bg-destructive' : 'bg-success')} style={{ width: `${Math.round(overallProgress * 100)}%` }} />
              </div>
              {!storeAvailable && !sentAll && (
                <p className="text-[11px] text-muted-foreground">Mantenha esta página aberta até terminar o envio.</p>
              )}
            </div>
          )}
          {missing.length > 0 && nextMissing ? (
            <>
              <p className="mb-2 text-center text-xs font-medium text-muted-foreground">Faltam {missing.length} item(ns) obrigatório(s)</p>
              <Button className="h-14 w-full rounded-2xl text-base font-medium shadow-xl disabled:opacity-60" disabled={busy} onClick={openNext}>
                {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : nextIsVisual ? <Camera className="mr-2 h-5 w-5" /> : <FileText className="mr-2 h-5 w-5" />}
                {nextIsVisual ? 'Fotografar' : 'Responder'} {nextMissing.item.name}
              </Button>
              <button type="button"
                className="mx-auto mt-2 block py-1 text-xs font-medium text-muted-foreground"
                onClick={() => toast('Suas respostas ficam salvas automaticamente')}>
                Salvar e continuar depois
              </button>
            </>
          ) : (
            <>
              {accPending.length > 0 && <p className="mb-2 text-center text-xs font-medium text-muted-foreground">Faltam {accPending.length} aceite(s)</p>}
              {hasPending && <p className="mb-2 text-center text-xs font-medium text-muted-foreground">Aguarde o envio de todos os arquivos para continuar</p>}
              <Button className="h-14 w-full rounded-2xl text-base font-medium shadow-xl disabled:opacity-60" disabled={busy || accPending.length > 0 || hasPending || missing.length > 0} onClick={submit}>
                {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Send className="mr-2 h-5 w-5" />} {d.version.settings.requires_review === false ? "Concluir vistoria" : "Enviar para revisão"}
              </Button>
            </>
          )}
        </div>
      )}
    </UploadCtx.Provider>
  );
}

/** Cartão de item: fotos em células da grade; demais tipos em linha inteira com o campo. */
function ItemCard({ it, pc, editable, geoPerItem, geoRequired, itemNumber, isNext, openRequest, onRequestHandled }: {
  it: PublicItem; pc: PC; editable: boolean; geoPerItem: boolean; geoRequired: boolean; itemNumber: number;
  isNext: boolean; openRequest: boolean; onRequestHandled: () => void;
}) {
  const uploads = useContext(UploadCtx)!;
  const type = it.item.field_type;
  const isFile = VISUAL_TYPES.includes(type);
  const cameraOnly = type === 'photo' && it.item.config.camera_only;
  const maxFiles = Math.max(1, Number(it.item.config.max_files) || 20);
  const [value, setValue] = useState<any>(it.current?.value ?? it.item.default_value ?? (type === 'multi_select' ? [] : ''));
  const [saving, setSaving] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLElement | null>(null);

  /** Salva valor e/ou novos arquivos (mantém anexos vigentes em itens com várias fotos). */
  const save = async (files: File[] = [], fromCamera = false) => {
    setSaving(true);
    try {
      const geo = geoPerItem ? await captureGeo() : null;
      if (geoPerItem && geoRequired && !geo) throw new Error('Permita o acesso à localização para salvar');
      const uploaded = [];
      for (const f of files) uploaded.push(await pc.uploadFile(it.answer_id, f, fromCamera));
      const kept = maxFiles > 1 ? it.attachments.map(({ url: _u, ...a }) => a) : [];
      let v: any = value;
      if (type === 'number') v = value === '' ? null : Number(value);
      if (isFile) v = null;
      await pc.act({ action: 'save_answer', answer_id: it.answer_id, value: v, geo, attachments: [...kept, ...uploaded].slice(-maxFiles) });
      toast.success('Item salvo');
      return true;
    } catch (e) {
      toast.error((e as Error).message || 'Erro ao salvar');
      return false;
    } finally { setSaving(false); }
  };

  /**
   * v1.15.0: coloca os arquivos na fila de segundo plano e libera a tela na hora.
   * A localização é capturada agora (momento da evidência), não no fim do envio.
   */
  const enqueue = (files: File[], fromCamera: boolean) => {
    if (geoPerItem) uploads.geo.set(it.answer_id, captureGeo());
    uploads.queue.add(it.answer_id, files, fromCamera, (f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null));
    return true;
  };

  const onFiles = (list: FileList | null) => {
    const files = Array.from(list ?? []).slice(0, maxFiles);
    if (fileRef.current) fileRef.current.value = '';
    if (files.some((f) => f.size > 25 * 1024 * 1024)) { toast.error('Cada arquivo deve ter até 25 MB'); return; }
    if (files.length) enqueue(files, false);
  };

  /** Botão principal rola até o item e abre a evidência/campo correspondente. */
  useEffect(() => {
    if (!openRequest) return;
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (editable) {
      if (type === 'file') fileRef.current?.click();
      else if (isFile) setEvidenceOpen(true);
    }
    onRequestHandled();
  }, [openRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rótulo e cores do estado do item
  const answered = isAnswered(it);
  const chip = it.review_status === 'rejected'
    ? { label: 'Refazer', cls: 'bg-destructive/10 text-destructive' }
    : it.review_status === 'approved'
      ? { label: 'Aprovada', cls: 'bg-success/10 text-success' }
      : answered
        ? { label: isFile ? 'Concluída' : 'Salvo', cls: 'bg-success/10 text-success' }
        : isNext && isFile
          ? { label: 'Próxima foto', cls: 'bg-warning/10 text-warning' }
          : { label: 'Pendente', cls: 'bg-muted text-muted-foreground' };

  // v1.15.0: estado dos envios em segundo plano deste item
  const mine = uploads.tasks.filter((t) => t.answerId === it.answer_id && t.status !== 'done');
  const failed = mine.filter((t) => t.status === 'error');
  const shownChip = failed.length
    ? { label: 'Falhou — tocar para reenviar', cls: 'bg-destructive/10 text-destructive' }
    : mine.length ? {
      label: `Enviando ${mine.length > 1 ? `${mine.length} fotos · ` : ''}${Math.round(aggregateProgress(mine) * 100)}%`,
      cls: 'bg-primary/10 text-primary',
    } : chip;
  const myProgress = mine.length && !failed.length ? aggregateProgress(mine) : null;

  // Miniatura: evidência enviada, senão foto local em envio, senão foto modelo, senão ícone
  const firstImage = it.attachments.find((a) => a.mime_type?.startsWith('image/') && a.url);
  const localPreview = mine.find((t) => t.previewUrl)?.previewUrl ?? null;
  const reference = type !== 'file' ? it.item.config.reference_photo?.url ?? null : null;
  const thumb = firstImage?.url ?? localPreview ?? reference ?? null;

  const cardRoot = cn('overflow-hidden rounded-3xl bg-card shadow-sm transition-all',
    it.review_status === 'rejected' && 'border-2 border-destructive/40',
    isNext && editable && 'ring-2 ring-warning shadow-lg',
    !isNext && !it.review_status.includes('rejected') && 'border border-border');

  if (isFile) {
    return (
      <article ref={rootRef} className={cn(cardRoot, 'relative', !editable && 'opacity-60')}>
        <button type="button" disabled={!editable || saving}
          onClick={() => (failed.length ? failed.forEach((t) => uploads.queue.retry(t.id))
            : type === 'file' ? fileRef.current?.click() : setEvidenceOpen(true))}
          className="block w-full p-3 text-left disabled:cursor-default">
          {/* Área da miniatura 4:3 com selo de estado no canto */}
          <div className={cn('relative mb-2.5 flex aspect-[4/3] items-center justify-center overflow-hidden rounded-2xl',
            thumb ? 'bg-muted' : isNext ? 'bg-warning/10' : it.review_status === 'rejected' ? 'bg-destructive/5' : 'bg-muted/60')}>
            {thumb && (firstImage?.url || localPreview)
              ? <img src={thumb} alt={it.item.name} loading="lazy" className="h-full w-full object-cover" />
              : thumb
                ? <img src={thumb} alt={`Foto modelo de ${it.item.name}`} loading="lazy" className="h-full w-full object-cover opacity-70" />
                : <Car className="h-12 w-12 text-muted-foreground/40" />}
            <span className={cn('absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full shadow-lg',
              failed.length ? 'bg-destructive text-destructive-foreground' : mine.length ? 'bg-primary text-primary-foreground'
                : answered ? 'bg-success text-success-foreground' : isNext ? 'bg-warning text-warning-foreground' : 'bg-muted-foreground/50 text-background')}>
              {failed.length ? <AlertTriangle className="h-4 w-4" /> : mine.length ? <Loader2 className="h-4 w-4 animate-spin" />
                : answered ? <Check className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
            </span>
            {/* v1.16.0: barrinha de progresso do envio na miniatura */}
            {myProgress !== null && (
              <div className="absolute inset-x-2 bottom-2 h-1.5 overflow-hidden rounded-full bg-background/70">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.round(myProgress * 100)}%` }} />
              </div>
            )}
            {it.attachments.length > 1 && (
              <span className="absolute bottom-2 left-2 rounded-full bg-foreground/70 px-2 py-0.5 text-[10px] font-medium text-background">
                +{it.attachments.length - 1}
              </span>
            )}
          </div>
          <p className="line-clamp-2 px-1 text-sm font-medium leading-snug text-foreground">
            {it.item.name}{it.item.is_required && <span className="text-destructive"> *</span>}
          </p>
          <span className={cn('mt-1.5 inline-flex rounded-lg px-2 py-1 text-[11px] font-medium', shownChip.cls)}>
            {shownChip.label}
          </span>
        </button>

        {it.review_status === 'rejected' && it.review_reason && (
          <p className="border-t border-destructive/20 bg-destructive/5 px-3 py-2 text-[11px] text-destructive">Motivo: {it.review_reason}</p>
        )}

        {/* Entrada de arquivo (usada em type=file e na gaveta de fotos) */}
        <input ref={fileRef} type="file" hidden multiple={maxFiles > 1}
          accept={type === 'photo' ? 'image/*' : undefined} onChange={(e) => onFiles(e.target.files)} />
        {(type === 'photo' || type === 'photo_or_file') && (
          <InspectionEvidenceDrawer open={evidenceOpen} onOpenChange={setEvidenceOpen}
            itemName={it.item.name} description={it.item.description}
            referenceUrl={it.item.config.reference_photo?.url}
            allowCamera allowFile={!cameraOnly}
            fileLabel={type === 'photo' ? 'Escolher foto da galeria' : 'Escolher foto ou documento'}
            loading={saving}
            onCamera={() => { setEvidenceOpen(false); setCameraOpen(true); }}
            onFile={() => { setEvidenceOpen(false); fileRef.current?.click(); }} />
        )}
        {/* v1.15.0: "Usar foto" fecha na hora; o envio segue em segundo plano (foto reduzida a 1920 px) */}
        <CameraCaptureDialog open={cameraOpen} onOpenChange={setCameraOpen} maxSide={1920} onCapture={(f) => enqueue([f], true)} />
      </article>
    );
  }

  // Item não visual: linha inteira com o campo na própria linha
  return (
    <article ref={rootRef} className={cn(cardRoot, 'col-span-2 p-4')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-snug text-foreground">{it.item.name}{it.item.is_required && <span className="text-destructive"> *</span>}</p>
          {it.item.description && <p className="mt-0.5 text-xs text-muted-foreground">{it.item.description}</p>}
        </div>
        <span className={cn('shrink-0 rounded-lg px-2 py-1 text-[11px] font-medium', chip.cls)}>{chip.label}</span>
      </div>

      {it.review_status === 'rejected' && it.review_reason && (
        <p className="mt-2 border-l-2 border-destructive pl-3 text-xs text-destructive">Motivo: {it.review_reason}</p>
      )}

      <div className="mt-3">
        <FieldInput type={type} value={value} onChange={setValue} options={it.item.config.options}
          placeholder={it.item.placeholder} disabled={!editable || saving} />
      </div>

      {editable && (
        <Button variant="secondary" className="mt-3 h-11 w-full rounded-xl font-semibold" disabled={saving} onClick={() => save()}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salvar resposta
        </Button>
      )}
    </article>
  );
}

/** Entrada de valor conforme o tipo do item. */
function FieldInput({ type, value, onChange, options, placeholder, disabled }: {
  type: string; value: any; onChange: (v: any) => void; options: string[]; placeholder: string | null; disabled: boolean;
}) {
  const chip = (active: boolean) => cn('h-11 rounded-xl border px-3 text-sm', active ? 'border-primary bg-primary/10 font-medium text-primary' : 'bg-background');
  if (type === 'yes_no') {
    return (
      <div className="grid grid-cols-2 gap-2">
        {[true, false].map((b) => <button key={String(b)} type="button" disabled={disabled} className={chip(value === b)} onClick={() => onChange(b)}>{b ? 'Sim' : 'Não'}</button>)}
      </div>
    );
  }
  if (type === 'single_select' || type === 'multi_select') {
    const multi = type === 'multi_select';
    const arr: string[] = Array.isArray(value) ? value : [];
    return (
      <div className="grid gap-2">
        {options.map((o) => {
          const active = multi ? arr.includes(o) : value === o;
          return <button key={o} type="button" disabled={disabled} className={cn(chip(active), 'text-left')}
            onClick={() => onChange(multi ? (active ? arr.filter((x) => x !== o) : [...arr, o]) : o)}>{o}</button>;
        })}
      </div>
    );
  }
  if (type === 'text') {
    return <Textarea value={value ?? ''} maxLength={5000} rows={3} placeholder={placeholder ?? ''} disabled={disabled}
      onChange={(e) => onChange(e.target.value.replace(/[<>]/g, ''))} />;
  }
  const inputType = type === 'number' ? 'number' : type === 'date' ? 'date' : type === 'time' ? 'time' : type === 'datetime' ? 'datetime-local' : 'text';
  return <Input className="h-11" type={inputType} inputMode={type === 'number' ? 'decimal' : undefined} value={value ?? ''}
    placeholder={placeholder ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
}
